import { FlightOps } from '../common/flight/FlightOpsTypes';
import { LogbookDutyMode } from '../common/flight/LogbookCustomData';
import { convertEvents, deduplicateResources, parseBlob } from '../fuelChain/reservationBlob';
import { isLegCancelled } from '../fuelChain/reservationFacts';
import { EventType, ResourceRole } from '../scheduling/types';
import { TypeOfFlight } from '../scheduling/types/FlightPlanTypes';
import { autocreateDuties } from './DutyAutoCreator';
import { deriveEffectiveTimes } from './effectiveTimes';
import { FinishedFlightLegLikeSource, } from './FinishedFlightLegLike';
import { DUTY_ITEM_STATE_OPEN } from './types';
export function parseDutyPreviewReservations(records) {
    var _a, _b, _c, _d;
    const out = [];
    for (const record of records) {
        if (record.deleted) {
            continue;
        }
        let blob;
        try {
            blob = parseBlob(record.data);
        }
        catch (_e) {
            continue;
        }
        if (!blob) {
            continue;
        }
        out.push({
            id: record.id,
            type: blob.type,
            dateFrom: new Date((_a = record.dateFrom) !== null && _a !== void 0 ? _a : ''),
            dateTo: new Date((_b = record.dateTo) !== null && _b !== void 0 ? _b : ''),
            deleted: (_c = record.deleted) !== null && _c !== void 0 ? _c : undefined,
            events: convertEvents(blob.events, record.id),
            resources: deduplicateResources((_d = blob.resources) !== null && _d !== void 0 ? _d : []),
            location: blob.location,
            locationEnd: blob.locationEnd,
            isQuotation: blob.isQuotation,
            slsCancelled: record.slsCancelled,
            opsCancelled: record.opsCancelled,
            cancelledLegs: [],
        });
    }
    return out;
}
export function isLogbookRecordForDuty(record) {
    var _a;
    if (record.legId || record.deleted) {
        return false;
    }
    if (!((_a = record.customData) === null || _a === void 0 ? void 0 : _a.duty) || record.customData.duty === LogbookDutyMode.sport) {
        return false;
    }
    return Boolean(record.departureTime && record.arrivalTime);
}
export const ReservationEventTypesToCreateDutyFrom = new Set([
    EventType.home_office,
    EventType.office,
    EventType.training,
    EventType.travel,
]);
export function reservationToFlightLegLike(reservation) {
    var _a, _b, _c;
    return {
        id: reservation.id,
        legId: reservation.id,
        timeOut: new Date(reservation.dateFrom),
        timeIn: new Date(reservation.dateTo),
        ops: FlightOps.CAT,
        airportOffIcao: (_a = reservation.location) !== null && _a !== void 0 ? _a : 'UNKN',
        airportOnIcao: (_c = (_b = reservation.locationEnd) !== null && _b !== void 0 ? _b : reservation.location) !== null && _c !== void 0 ? _c : 'UNKN',
        source: FinishedFlightLegLikeSource.scheduling,
        reservationEventType: reservation.type,
    };
}
export function childEventToFlightLegLike(childEvent, reservation, flightInfo) {
    var _a;
    const isCancelled = isLegCancelled({ childEvent, reservation });
    const eff = deriveEffectiveTimes(new Date(childEvent.dateFrom), new Date(childEvent.dateTo), flightInfo, isCancelled, (_a = reservation.flightData) === null || _a === void 0 ? void 0 : _a.flights.find((flight) => flight.childEventId === childEvent.id));
    return {
        id: childEvent.id,
        legId: childEvent.id,
        timeOut: eff.effectiveTimeOut,
        timeIn: eff.effectiveTimeIn,
        ops: childEvent.typeOfFlight === TypeOfFlight.N
            ? FlightOps.CAT
            : childEvent.typeOfFlight === TypeOfFlight.X
                ? FlightOps.ATO_NCO
                : FlightOps.NCO,
        airportOffIcao: childEvent.location || 'UNKN',
        airportOnIcao: childEvent.locationEnd || 'UNKN',
        plannedFdpExtension: childEvent.plannedFdpExtension,
        source: reservation.isQuotation
            ? FinishedFlightLegLikeSource.quotation
            : FinishedFlightLegLikeSource.scheduling,
    };
}
export function findContactInReservation(reservation, contactId) {
    if (reservation.resources.some((resource) => resource.resource.resourceId === contactId && !resource.deleted)) {
        return true;
    }
    if (reservation.events) {
        for (const event of reservation.events) {
            if (event.resources.some((resource) => resource.resource.resourceId === contactId && !resource.deleted)) {
                return true;
            }
        }
    }
    return false;
}
const isContactsDutyResource = (resource, contactId) => resource.resource.resourceId === contactId && !resource.deleted && resource.role !== ResourceRole.person;
export function reservationsToDutyLegLikes(reservations, contactId) {
    return reservations
        .filter((r) => !r.slsCancelled && !r.opsCancelled)
        .filter((r) => {
        var _a;
        return r.resources.some((res) => isContactsDutyResource(res, contactId)) ||
            ((_a = r.events) === null || _a === void 0 ? void 0 : _a.some((evt) => evt.resources.some((res) => isContactsDutyResource(res, contactId))));
    })
        .flatMap((r) => {
        if (ReservationEventTypesToCreateDutyFrom.has(r.type)) {
            return [reservationToFlightLegLike(r)];
        }
        if (r.events) {
            return r.events
                .filter((evt) => {
                if (evt.deleted) {
                    return false;
                }
                if (!evt.resources.some((res) => isContactsDutyResource(res, contactId))) {
                    return false;
                }
                return true;
            })
                .map((evt) => childEventToFlightLegLike(evt, r));
        }
        return [];
    });
}
export function buildLegPlanMap(reservations) {
    var _a;
    const map = new Map();
    for (const reservation of reservations) {
        const events = (_a = reservation.events) !== null && _a !== void 0 ? _a : [];
        if (!events.length) {
            setIfValid(map, reservation.id, reservation.dateFrom);
        }
        for (const event of events) {
            setIfValid(map, event.id, event.dateFrom, event.plannedFdpExtension);
        }
    }
    return map;
}
function setIfValid(map, id, dateFrom, plannedFdpExtension) {
    if (!id || dateFrom === undefined || dateFrom === null) {
        return;
    }
    const date = new Date(dateFrom);
    if (!Number.isNaN(date.getTime())) {
        map.set(id, { timeOut: date, plannedFdpExtension });
    }
}
export function attachLegPlans(legs, legPlans) {
    if (!legPlans.size) {
        return legs;
    }
    return legs.map((leg) => {
        const key = leg.reservationEventId || leg.reservationId;
        const plan = key ? legPlans.get(key) : undefined;
        return plan
            ? Object.assign(Object.assign(Object.assign({}, leg), { plannedTimeOut: plan.timeOut }), (plan.plannedFdpExtension ? { plannedFdpExtension: true } : {})) : leg;
    });
}
export const getCandidateLegsForAutomaticDuties = (legs, duties) => {
    const legsFromNewest = [...legs].sort((a, b) => b.timeOut.getTime() - a.timeOut.getTime());
    const existingIds = new Set(duties.flatMap((d) => [...d.flightLegIds, ...d.logbookRecordIds]));
    const firstLegWithoutDutyIndex = legsFromNewest.findIndex((l) => existingIds.has(l.id));
    return firstLegWithoutDutyIndex >= 0 ? legsFromNewest.slice(0, firstLegWithoutDutyIndex) : legsFromNewest;
};
export const getCandidateScheduleEventsForAutomaticDuties = (scheduleEvents, legs, duties) => {
    var _a, _b;
    const newestLegEnd = (_a = legs[0]) === null || _a === void 0 ? void 0 : _a.timeIn;
    const newestDutyEnd = (_b = duties[0]) === null || _b === void 0 ? void 0 : _b.endDate;
    const newestEnd = newestLegEnd && newestDutyEnd
        ? new Date(Math.max(newestLegEnd.getTime(), newestDutyEnd.getTime()))
        : newestLegEnd || newestDutyEnd;
    const eventsSortedFromNewest = [...scheduleEvents].sort((a, b) => b.timeOut.getTime() - a.timeOut.getTime());
    if (newestEnd) {
        const index = eventsSortedFromNewest.findIndex((e) => e.timeOut.getTime() < newestEnd.getTime());
        return index >= 0 ? eventsSortedFromNewest.slice(0, index) : eventsSortedFromNewest;
    }
    return eventsSortedFromNewest;
};
export var DutyPreviewSource;
(function (DutyPreviewSource) {
    DutyPreviewSource["flight"] = "flight";
    DutyPreviewSource["quotation"] = "quotation";
    DutyPreviewSource["schedule"] = "schedule";
})(DutyPreviewSource || (DutyPreviewSource = {}));
export function createPreviewDutyItems({ contact, legs, scheduleLegLikes, dutyItems, legPlans, sources, extraLegLikes, }) {
    const legsForAutomaticDuties = getCandidateLegsForAutomaticDuties(attachLegPlans(legs, legPlans), dutyItems);
    const reservationsForAutomaticDuties = getCandidateScheduleEventsForAutomaticDuties(scheduleLegLikes, legsForAutomaticDuties, dutyItems);
    let previewLegs = [];
    if (sources.includes(DutyPreviewSource.schedule)) {
        previewLegs.push(...reservationsForAutomaticDuties.filter((l) => l.source === FinishedFlightLegLikeSource.scheduling));
    }
    if (sources.includes(DutyPreviewSource.quotation)) {
        previewLegs.push(...reservationsForAutomaticDuties.filter((l) => l.source === FinishedFlightLegLikeSource.quotation));
    }
    if (sources.includes(DutyPreviewSource.flight)) {
        previewLegs.push(...legsForAutomaticDuties);
    }
    if (extraLegLikes) {
        const extraLegIds = new Set(extraLegLikes.map((l) => l.id));
        if (extraLegIds.size) {
            previewLegs = previewLegs.filter((l) => !extraLegIds.has(l.id));
        }
        previewLegs.push(...extraLegLikes.filter((l) => !l.forceDeleted));
    }
    return autocreateDuties(previewLegs, contact);
}
const statusBySource = (source) => {
    switch (source) {
        case FinishedFlightLegLikeSource.quotation: {
            return 'quotation';
        }
        case FinishedFlightLegLikeSource.scheduling: {
            return 'scheduled';
        }
        case undefined: {
            return undefined;
        }
        default: {
            return 'preview';
        }
    }
};
function getPreviewItemSourceStatus(item, legLikes) {
    var _a;
    const legLike = [...item.flightLegIds, ...item.logbookRecordIds]
        .map((id) => legLikes.get(id))
        .find((l) => l !== undefined);
    return (_a = statusBySource(item.source)) !== null && _a !== void 0 ? _a : statusBySource(legLike === null || legLike === void 0 ? void 0 : legLike.source);
}
export function getDutyStatuses(lineItems, previewItemIds, legLikes) {
    var _a;
    const previewStatuses = lineItems.map((item) => { var _a; return previewItemIds.has(item.id) ? ((_a = getPreviewItemSourceStatus(item, legLikes)) !== null && _a !== void 0 ? _a : null) : undefined; });
    const allPreview = lineItems.length > 0 && previewStatuses.every((status) => status !== undefined);
    const previewLineStatus = (_a = previewStatuses.find((status) => !!status)) !== null && _a !== void 0 ? _a : 'preview';
    const persistedStatus = (item) => (item.state === DUTY_ITEM_STATE_OPEN ? 'open' : 'closed');
    const items = lineItems.map((item, index) => {
        const previewStatus = previewStatuses[index];
        if (previewStatus === undefined) {
            return persistedStatus(item);
        }
        return previewStatus !== null && previewStatus !== void 0 ? previewStatus : previewLineStatus;
    });
    if (!lineItems.length) {
        return { line: null, items: [] };
    }
    const line = allPreview
        ? previewLineStatus
        : lineItems.some((item) => item.state === DUTY_ITEM_STATE_OPEN)
            ? 'open'
            : 'closed';
    return { line, items };
}
//# sourceMappingURL=dutyPreviews.js.map